// Validates the Durations format: a comma-separated list of positive integers ("N") or ranges
// ("N-M", strictly N < M), in strictly ascending order, without duplicates, overlaps, leading
// zeros, or whitespace. Durations is optional - callers should skip calling this for an empty
// (post-trim) value; an empty string is not itself considered valid here.
const SEGMENT_PATTERN = /^(\d+)(?:-(\d+))?$/;

interface IDurationsSegment {
  from: number;
  to: number;
}

function isValidNumber(value: string): boolean {
  // Rejects leading zeros (e.g. "01"), and 0 itself since durations must be positive.
  return value === String(Number(value)) && Number(value) > 0;
}

function parseSegment(segment: string): IDurationsSegment | undefined {
  const match = SEGMENT_PATTERN.exec(segment);
  if (!match) {
    return undefined;
  }

  const [, fromText, toText] = match;
  if (!isValidNumber(fromText) || (toText !== undefined && !isValidNumber(toText))) {
    return undefined;
  }

  const from = Number(fromText);
  const to = toText === undefined ? from : Number(toText);

  if (toText !== undefined && to <= from) {
    return undefined;
  }

  return { from, to };
}

export function isValidDurations(value: string): boolean {
  if (!value) {
    return false;
  }

  const rawSegments = value.split(',');
  const segments: IDurationsSegment[] = [];

  for (const rawSegment of rawSegments) {
    const segment = parseSegment(rawSegment);
    if (!segment) {
      return false;
    }

    segments.push(segment);
  }

  for (const [index, segment] of segments.entries()) {
    const previous = segments[index - 1];
    if (previous && segment.from <= previous.to) {
      return false;
    }
  }

  return true;
}
